'use client';

import { useMemo } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { Text } from '../../../../components/styled/text';
import { MarkdownPreview } from '../../../markdown-preview';
import { SmartValue } from './EntityBadge';
import {
  normalizeHttpHeaders,
  resolveEffectiveHttpVersion,
} from '@terra/shared-utils';

type HttpRequestRendererProps = {
  args: Record<string, unknown>;
  compact?: boolean;
};

type ParsedHttpRequest = {
  method: string;
  url: string;
  headers?: Record<string, string>;
  body?: string;
  credentialId?: string;
};

const REQUEST_LABEL = 'Request';

function parseHttpArgs(
  args: Record<string, unknown>,
): ParsedHttpRequest | null {
  if (!args.url || typeof args.url !== 'string') {
    return null;
  }

  // Handle both formats:
  // 1. http-tool: { url, credentialId, options: { method, headers, body } }
  // 2. dynamic-http-tool: { url, method, headers, body, credentialId }
  const options =
    args.options && typeof args.options === 'object'
      ? (args.options as Record<string, unknown>)
      : undefined;

  // Build final URL with query params if present
  let finalUrl = args.url as string;
  const queryParams =
    (options &&
      (options.queryParams || options.params || options.searchParams)) ||
    args.queryParams ||
    args.params ||
    args.searchParams;

  if (
    queryParams &&
    typeof queryParams === 'object' &&
    !Array.isArray(queryParams)
  ) {
    try {
      const url = new URL(finalUrl);
      Object.entries(queryParams as Record<string, unknown>).forEach(
        ([key, value]) => {
          url.searchParams.append(key, String(value));
        },
      );
      finalUrl = url.toString();
    } catch {
      // Keep original URL if parsing fails
    }
  }

  // Extract method
  const method =
    typeof options?.method === 'string'
      ? options.method
      : typeof args.method === 'string'
        ? args.method
        : 'GET';

  const contentType =
    typeof args.contentType === 'string' ? args.contentType : undefined;

  let headers =
    options?.headers && typeof options.headers === 'object'
      ? (options.headers as Record<string, string>)
      : args.headers && typeof args.headers === 'object'
        ? (args.headers as Record<string, string>)
        : undefined;

  if (contentType) {
    const hasContentTypeHeader = headers
      ? Object.keys(headers).some((key) => key.toLowerCase() === 'content-type')
      : false;

    if (!hasContentTypeHeader) {
      headers = { 'Content-Type': contentType, ...(headers ?? {}) };
    }
  }

  // Helper to convert object to URL-encoded string
  const toUrlEncoded = (obj: Record<string, unknown>): string => {
    return Object.entries(obj)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value ?? ''))}`,
      )
      .join('&');
  };

  // Helper to check if content-type is form-urlencoded
  const isFormUrlEncoded = (): boolean => {
    if (!headers) return false;
    const contentType = Object.entries(headers).find(
      ([key]) => key.toLowerCase() === 'content-type',
    );
    return (
      contentType?.[1]?.toLowerCase().includes('x-www-form-urlencoded') ?? false
    );
  };

  // Extract body - handle multiple formats
  let body: string | undefined;
  const rawBody = options?.body ?? args.body ?? options?.data ?? args.data;
  const rawFormData = options?.formData ?? args.formData;

  if (rawBody) {
    if (typeof rawBody === 'string') {
      body = rawBody;
    } else if (typeof rawBody === 'object' && rawBody !== null) {
      // For form-urlencoded, convert object to URL-encoded string
      if (isFormUrlEncoded()) {
        body = toUrlEncoded(rawBody as Record<string, unknown>);
      } else {
        body = JSON.stringify(rawBody, null, 2);
      }
    }
  } else if (rawFormData) {
    if (typeof rawFormData === 'string') {
      body = rawFormData;
    } else if (typeof rawFormData === 'object' && rawFormData !== null) {
      // Form data is typically URL-encoded
      body = toUrlEncoded(rawFormData as Record<string, unknown>);
    }
  }

  // Extract credentialId
  const credentialId =
    typeof args.credentialId === 'string' ? args.credentialId : undefined;

  return {
    method: method.toUpperCase(),
    url: finalUrl,
    headers,
    body,
    credentialId,
  };
}

// Mask sensitive header values
function maskSensitiveValue(key: string, value: string): string {
  if (
    key.toLowerCase() === 'authorization' &&
    value.toLowerCase().startsWith('bearer ')
  ) {
    return 'Bearer <token>';
  }
  return value;
}

// Format request as HTTP markdown code block
function formatRequestAsHttp(request: ParsedHttpRequest): string {
  let displayPath = '/';
  let host = '';

  try {
    const url = new URL(request.url);
    displayPath = url.pathname + url.search;
    host = url.host;
  } catch {
    displayPath = request.url;
  }

  const mergedHeaders = host
    ? { Host: host, ...(request.headers ?? {}) }
    : (request.headers ?? {});
  const normalizedHeaders = normalizeHttpHeaders(mergedHeaders);
  const headerLines = Object.entries(normalizedHeaders).map(([key, value]) => {
    const headerValue = Array.isArray(value) ? value.join(', ') : value;
    return `${key}: ${maskSensitiveValue(key, headerValue)}`;
  });

  const httpVersion = resolveEffectiveHttpVersion({
    headers: request.headers,
  });
  let content = `${request.method} ${displayPath} ${httpVersion}`;
  if (headerLines.length > 0) {
    content += `\n${headerLines.join('\n')}`;
  }
  if (request.body) {
    content += `\n\n${request.body}`;
  }

  return `\`\`\`http\n${content}\n\`\`\``;
}

/**
 * Renders HTTP request arguments in a formatted view
 * Shows the request as HTTP syntax with optional credential badge
 */
export function HttpRequestRenderer({
  args,
  compact = false,
}: HttpRequestRendererProps) {
  const parsedRequest = useMemo(() => parseHttpArgs(args), [args]);

  if (!parsedRequest) {
    return null;
  }

  const requestMarkdown = formatRequestAsHttp(parsedRequest);

  if (compact) {
    // Compact: just show method and URL
    return (
      <Text fontSize="xs" color="fg.muted">
        {parsedRequest.method} {parsedRequest.url}
      </Text>
    );
  }

  return (
    <VStack width="100%" gap={2} alignItems="stretch">
      {parsedRequest.credentialId && (
        <SmartValue
          fieldName="credentialId"
          value={parsedRequest.credentialId}
        />
      )}
      <Box width="100%">
        <Box
          px={3}
          py={2}
          bg="bg.muted"
          borderBottomWidth="1px"
          borderColor="border.default"
          borderTopRadius="md"
        >
          <Text fontSize="xs" fontWeight="medium" color="fg.muted">
            {REQUEST_LABEL}
          </Text>
        </Box>
        <Box
          bg="bg.subtle"
          borderBottomRadius="md"
          overflow="hidden"
          css={{ '& pre': { overflowX: 'hidden' } }}
        >
          <MarkdownPreview source={requestMarkdown} fontSize={12} compact />
        </Box>
      </Box>
    </VStack>
  );
}
